// components
import {
  CalendarIcon,
  DateRangeValidationError,
  DesktopDateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickerChangeHandlerContext
} from '@mui/x-date-pickers-pro';
import { StyledMonthOrWeekRangeBox } from './MonthOrWeekRange.styles';

// hooks
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

// types
import { MonthOrWeekRangeProps } from './MonthOrWeekRange.types';
import { PickerRangeValue } from '@mui/x-date-pickers/internals/models/value';

// utils
import {
  errorMessage,
  maxDate,
  minDate,
  shouldDisableDateInMonthOrWeek
} from './MonthOrWeekRange.utils';
import { RangeScope } from './MonthOrWeekRange.consts';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import 'dayjs/locale/nl';
dayjs.locale('nl');

export default function MonthOrWeekRange({
  isSetToMonth,
  setDateRange
}: MonthOrWeekRangeProps) {
  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.realization'
  });
  const [error, setError] = useState<DateRangeValidationError>([null, null]);
  const [date, setDate] = useState<PickerRangeValue>([null, null]);

  const [startDateError, endDateError] = error;

  const handleDateRange = (
    range: PickerRangeValue,
    _context: PickerChangeHandlerContext<DateRangeValidationError>
  ) => {
    let [start, end] = range;

    const validStart = dayjs(start).isValid();
    const validEnd = dayjs(end).isValid();

    if (isSetToMonth) {
      if (validStart) start = dayjs(start).startOf('month');
      if (validEnd) end = dayjs(end).endOf('month');
    }

    setDate([start, end]);
  };

  const helperFor = (pos: 'start' | 'end') =>
    errorMessage({
      errorType: pos === 'start' ? startDateError : endDateError,
      scope: isSetToMonth ? RangeScope.MONTH : RangeScope.WEEK,
      position: pos
    });

  useEffect(() => {
    if (error.every((e) => !e)) {
      setDateRange([
        date[0] ? dayjs(date[0]).toDate() : null,
        date[1] ? dayjs(date[1]).toDate() : null
      ]);
    } else {
      setDateRange([null, null]);
    }
  }, [error, date, setDateRange]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
      <StyledMonthOrWeekRangeBox>
        <DesktopDateRangePicker
          displayWeekNumber
          minDate={dayjs(minDate)}
          maxDate={dayjs(maxDate)}
          onError={(error) => setError(error)}
          value={date}
          onChange={handleDateRange}
          format={isSetToMonth ? 'MMMM YYYY' : 'DD-MM-YYYY'}
          shouldDisableDate={(day, position) => {
            if (day < minDate || day > maxDate) {
              return false;
            }

            return shouldDisableDateInMonthOrWeek({
              isSetToMonth,
              day,
              position
            });
          }}
          localeText={{
            start: isSetToMonth
              ? t('monthOrWeekRange.startOfMonth')
              : t('monthOrWeekRange.startOfWeek'),
            end: isSetToMonth
              ? t('monthOrWeekRange.endOfMonth')
              : t('monthOrWeekRange.endOfWeek'),
            clearButtonLabel: t('monthOrWeekRange.clear')
          }}
          slots={{
            openPickerIcon: CalendarIcon,
            field: MultiInputDateRangeField
          }}
          slotProps={{
            textField: (arg) => {
              const { position } = arg as unknown as {
                position: 'start' | 'end';
              };
              return {
                variant: 'outlined',
                size: 'small',
                helperText: helperFor(position),
                InputProps: { endAdornment: <CalendarIcon /> }
              };
            },
            field: {
              dateSeparator: t('monthOrWeekRange.until')
            },
            actionBar: {
              actions: ['clear']
            }
          }}
        />
      </StyledMonthOrWeekRangeBox>
    </LocalizationProvider>
  );
}
